import { cloneDeep, forEach, isArray, keyBy, map, union } from "lodash";

import { GLOBAL_FILTER_ID } from "../../constants/identifiers";
import settingsSelectors from "../../features/settingsModal/selectors";
import batchOperations from "../../modules/batchOperations";
import chainReducer, { batchActions } from "../../modules/chainReducer";
import definitionReducer, { definitionSelectors } from "../../modules/definition";
import { referenceOperations } from "../../modules/definition/references";
import { typeOperations } from "../../modules/definition/types";
import sortFunctions from "../../modules/items/sortFunctions";
import notificationMessages from "../../notificationMessages";
import { appState } from "../../store/appState";
import novel from "../../templates/fiction/novel";
import { createFromJS } from "../../types";
import { PointRecord } from "../../types/immutableRecords/helper/PointRecord";
import { TimelineViewOptionsRecord } from "../../types/immutableRecords/model/appState/TimelineViewOptionsRecord";
import upgradeDefinitions from "./upgradeDefinitions";
import upgradeDependency from "./upgradeDependency";
import upgradeDependencyToConstraint from "./upgradeDependencyToConstraint";
import upgradeEntity from "./upgradeEntity";
import upgradeEvent from "./upgradeEvent";
import upgradeSync from "./upgradeSync";

const dispatchActions = (store, actions) => {
  const performAction = isArray(actions) ? batchActions(actions) : actions;
  const newStore = store.set("definitions", chainReducer(definitionReducer)(store.definitions, performAction, store));
  return newStore;
};

const excludeUnnecessaryTypesAndReferences = (store, defaultSequenceType) => {
  const allTypes = settingsSelectors.getAllOrderedTypes(store);
  const allReferences = settingsSelectors.getAllOrderedReferences(store);
  const eventTypes = settingsSelectors.eventTypesFromAllTypes(allTypes);
  const personTypes = settingsSelectors.personTypesFromAllTypes(allTypes);
  const filteredEventTypes = eventTypes.filter(type => type.id != defaultSequenceType);
  const otherTypesExcludingNarrativeFolder = settingsSelectors.otherTypesFromAllTypesExcludingNarrativeFolder(allTypes);
  const eventToPersonReferences = settingsSelectors.eventToPersonReferences(allReferences, eventTypes, personTypes);
  const eventToEventReferences = settingsSelectors
    .eventToEventReferences(allReferences, eventTypes)
    .filter(ref => !eventToPersonReferences.find(ref2 => ref2.id === ref.id));
  const personToPersonReferences = settingsSelectors
    .personToPersonReferences(allReferences, personTypes)
    .filter(ref => !eventToPersonReferences.find(ref2 => ref2.id === ref.id));
  const typeAction = typeOperations.changeEnabled(
    filteredEventTypes.concat(otherTypesExcludingNarrativeFolder).map(type => type.id),
    false
  );
  const eventToEventAction = referenceOperations.changeEnabled(
    eventToEventReferences.map(ref => ref.id),
    false
  );
  const personToPersonAction = referenceOperations.changeEnabled(
    personToPersonReferences.map(ref => ref.id),
    false
  );
  return dispatchActions(store, [typeAction, eventToEventAction, personToPersonAction]);
};

const updateTimelineOptions = (viewsById, timelineOptions) => {
  for (const viewId in viewsById) {
    viewsById[viewId]["timelineOptions"] = timelineOptions;
  }
  return viewsById;
};

const updateViewTimelineOptions = (viewsByIdArray, v2Data) => {
  const point = PointRecord({
    x: parseInt(v2Data.uiState.ScrollPosition),
    y: parseInt(v2Data.uiState.ScrollPositionY)
  });
  const timelineOptions = TimelineViewOptionsRecord({
    scrollPosition: point,
    unitScale: parseFloat(v2Data.uiState.UnitScale)
  }).toJS();

  viewsByIdArray.forEach(byId => updateTimelineOptions(byId, timelineOptions));
};

// Fill in so that all allowed columns are shown by default initially.
const updateViewRelationshipColumns = (viewsByIdArray, data, definitions) => {
  let columnIds = [];
  forEach(data.items.byId, obj => {
    if (definitions.types.byId[obj.type].showAsColumn) {
      columnIds.push(obj.id);
    }
  });
  viewsByIdArray.forEach(byId => {
    forEach(byId, v => {
      v.relationshipOptions.columnIds = columnIds;
    });
  });
};

const updateViews = (v2Data, appState, data, definitions) => {
  const viewsByIdArray = [
    appState.desktopOpenViews.byId,
    appState.tabletOpenViews.byId,
    appState.mobileLandscapeOpenViews.byId
  ];
  updateViewTimelineOptions(viewsByIdArray, v2Data);
  viewsByIdArray.pop();
  updateViewRelationshipColumns(viewsByIdArray, data, definitions);
};

/**
 *
 * @param {object} v2Data
 * @param {TimelineDefinitionsRecord} v3DefinitionsRecord
 * @param {string} selectedSyncProject
 * @param {boolean} createConstraintsFromV2Dependencies
 */

const upgradeV2DataWithV3Template = (
  v2Data,
  v3TimelineRecord,
  selectedSyncProject,
  createConstraintsFromV2Dependencies,
  enabledOtherTypeIds
) => {
  const v3DefinitionsRecord = v3TimelineRecord.definitions;
  const {
    definitions,
    eventSummaryProperty,
    newPropIdForV2Id,
    newTypeIdForV2Id,
    newReferenceIdForV2Id,
    defaultEventTypeId,
    updateDatesInBC
  } = upgradeDefinitions(v2Data.template, v3DefinitionsRecord, enabledOtherTypeIds);

  const calendarRecord = createFromJS.CalendarRecord(definitions.calendar);
  const templateItems = v3TimelineRecord.data.items.byId.toList().toJS();

  const maxDisplayId = {};

  let dateConstraints = [];

  const entities = map(v2Data.entities, e =>
    upgradeEntity(
      e,
      definitions,
      newTypeIdForV2Id,
      maxDisplayId,
      v2Data,
      updateDatesInBC,
      calendarRecord,
      dateConstraints
    )
  );

  entities.sort((a, b) => a.sortOrder > b.sortOrder);
  const entitiesById = keyBy(entities, e => e.id);
  let relationships = [];

  let attachments = [];

  // Rather than trusting the tags array stored in v2Data.tags, we will build it again
  // from the individual events. That way, we import every tag in events, and don't import
  // any that are not used.
  let inverseTagLookup = {};
  let tags = {};

  let dependencies = [];

  // Iterate through all events and convert their V2 dependencies into either constraints or dependencies
  if (createConstraintsFromV2Dependencies) {
    dateConstraints = dateConstraints.concat(map(v2Data.dependencies, v2Dep => upgradeDependencyToConstraint(v2Dep)));
  } else {
    dependencies = map(v2Data.dependencies, v2Dep => upgradeDependency(v2Dep));
  }

  const events = map(v2Data.events, (e, index) =>
    upgradeEvent(
      e,
      index,
      definitions,
      defaultEventTypeId,
      eventSummaryProperty,
      tags,
      inverseTagLookup,
      relationships,
      entitiesById,
      attachments,
      newPropIdForV2Id,
      newReferenceIdForV2Id,
      dependencies,
      dateConstraints,
      updateDatesInBC,
      calendarRecord
    )
  );

  const sequence = [...v3TimelineRecord.data.sequence.toArray(), ...map(events.sort(sortFunctions.byStart), e => e.id)];

  const mindmapId = "initialMindmap";
  let mindmaps = {
    byId: {},
    allIds: [mindmapId]
  };
  mindmaps.byId[mindmapId] = {
    id: mindmapId,
    label: "Mindmap 1"
  };

  let narrative = {
    children: []
  };

  const items = union(entities, events, templateItems);
  const calendarMarkers = v3TimelineRecord.data.calendarMarkers.toJS();

  let allIdsForType = {};
  forEach(definitions.types.allIds, id => (allIdsForType[id] = []));

  forEach(items, obj => {
    const t = obj.type;
    const id = obj.id;
    if (allIdsForType[t]) {
      allIdsForType[t].push(id);
    } else {
      allIdsForType[t] = [id];
    }
  });

  let viewsById = appState.desktopOpenViews.byId;

  let filterIds = Object.keys(viewsById);
  filterIds.push(GLOBAL_FILTER_ID);

  const filterObjects = map(filterIds, id => ({ id }));
  const filters = {
    byId: keyBy(filterObjects, f => f.id),
    allIds: filterIds
  };

  const data = {
    items: {
      byId: keyBy(items, e => e.id),
      allIds: items.map(e => e.id),
      allIdsForType
    },
    relationships: {
      byId: keyBy(relationships, r => r.id),
      allIds: relationships.map(r => r.id)
    },
    attachments: {
      byId: keyBy(attachments, a => a.id),
      allIds: attachments.map(a => a.id)
    },
    mindmaps,
    narrative,
    sequence,
    calendarMarkers,
    filters,
    tags,
    dateConstraints: {
      byId: keyBy(dateConstraints, c => c.id),
      allIds: dateConstraints.map(c => c.id)
    },
    dependencies: {
      byId: keyBy(dependencies, c => c.id),
      allIds: dependencies.map(c => c.id)
    }
  };

  let useAppState = cloneDeep(appState);

  updateViews(v2Data, useAppState, data, definitions);

  let { sync, furtherSyncConfigRequired } = upgradeSync(
    v2Data,
    selectedSyncProject,
    definitions,
    eventSummaryProperty,
    newReferenceIdForV2Id
  );
  let uiState = {};
  if (furtherSyncConfigRequired) {
    uiState.floatingNotification = notificationMessages.syncSettingsNeedsFurtherConfiguration;
  }

  const result = {
    definitions,
    data,
    appState: useAppState,
    uiState,
    filters,
    savedFilters: {}
  };

  if (sync) {
    result.sync = sync;
  }

  return result;
};

export const convertV2DataForReadonly = v2Data => {
  let newStore = createFromJS.TimelineRecord({ definitions: novel });
  const defaultNarrativeType = definitionSelectors.getDefaultNarrativeDocumentType(newStore);
  const defaultSequenceType = definitionSelectors.getDefaultSequenceType(newStore);
  // Do not include narrative
  newStore = dispatchActions(
    newStore,
    batchOperations.changeIncludeNarrativeAndIncludeType(false, defaultNarrativeType, defaultSequenceType)
  );

  // Exclude unnecessary types and references
  newStore = excludeUnnecessaryTypesAndReferences(newStore, defaultSequenceType);

  const upgradedTimelineData = upgradeV2DataWithV3Template(v2Data, newStore, [], true, []);

  return upgradedTimelineData;
};

export default upgradeV2DataWithV3Template;
